import React from "react";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";

// --- Type Definitions ---
export interface IWhoWeAreItem1 {
  Title: string;
  Description: string;
}
export interface IWhoWeAreItem2 {
  Description: string;
  LinkTitle: string;
  Link: string;
}
export interface IWhoWeAreV1Widget {
  type: string; // assumed to be WidgetType.WhoWeAreV1 in parent
  Items: [IWhoWeAreItem1, IWhoWeAreItem2];
}

// --- Props for the WhoWeAreV1Item component ---
interface WhoWeAreV1ItemProps {
  item: IWhoWeAreV1Widget;
  error: any;
  onChange: (updatedItem: IWhoWeAreV1Widget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const WhoWeAreV1Item: React.FC<WhoWeAreV1ItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Extract the two sections from the Items tuple.
  const section1: IWhoWeAreItem1 = item.Items && item.Items[0]
    ? item.Items[0]
    : { Title: "", Description: "" };
  const section2: IWhoWeAreItem2 = item.Items && item.Items[1]
    ? item.Items[1]
    : { Description: "", LinkTitle: "", Link: "" };

  // Helper to safely extract a value from an event or use the raw value.
  const getValue = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ): string => (typeof e === "string" ? e : e.target.value);

  // Handler for Section 1 fields.
  const handleSection1Change = (
    field: keyof IWhoWeAreItem1,
    value: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(value);
    const updatedSection1 = { ...section1, [field]: newValue };
    onChange({ ...item, Items: [updatedSection1, section2] }, "Items");
  };

  // Handler for Section 2 fields.
  const handleSection2Change = (
    field: keyof IWhoWeAreItem2,
    value: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(value);
    const updatedSection2 = { ...section2, [field]: newValue };
    onChange({ ...item, Items: [section1, updatedSection2] }, "Items");
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Who We Are Widget
        </Typography>

        {/* Section 1 */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Section 1
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <GenericInput
              type="text"
              name="Title"
              title="Title"
              value={section1.Title}
              error={error?.[0]?.Title || ""}
              onChange={(e) => handleSection1Change("Title", e)}
              onBlur={() => onblur("Title", 0)}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <GenericTextAreaInput
              name="Description"
              title="Description"
              value={section1.Description}
              error={error?.[0]?.Description || ""}
              onChange={(e) => handleSection1Change("Description", e)}
              onBlur={() => onblur("Description", 0)}
              disabled={false}
            />
          </Grid>
        </Grid>

        {/* Section 2 */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Section 2
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <GenericTextAreaInput
              name="Description"
              title="Description"
              value={section2.Description}
              error={error?.[1]?.Description || ""}
              onChange={(e) => handleSection2Change("Description", e)}
              onBlur={() => onblur("Description", 1)}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="LinkTitle"
              title="Link Title"
              value={section2.LinkTitle}
              error={error?.[1]?.LinkTitle || ""}
              onChange={(e) => handleSection2Change("LinkTitle", e)}
              onBlur={() => onblur("LinkTitle", 1)}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="Link"
              title="Link"
              value={section2.Link}
              error={error?.[1]?.Link || ""}
              onChange={(e) => handleSection2Change("Link", e)}
              onBlur={() => onblur("Link", 1)}
              disabled={false}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default WhoWeAreV1Item;
